import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNotify } from '@/components/snackbar';
import FormProvider from '@/components/hook-form/FormProvider';
import Scrollbar from '@/components/scrollbar';
import RHFTextField from '@/components/hook-form/RHFTextField';
import { RHFAsyncAutocomplete, RHFMultiSelect, RHFSelect } from '@/components/hook-form';
import Iconify from '@/components/iconify';
import { getProductsLookupService } from '@/services/products';
import { FormScopes } from '@/utils/form';
import { formatProductNameWithStatus } from '@/utils/productStatus';
import useAppsIntegrationForm from '@/sections/@dashboard/apps/AppsIntegrationForm/useAppsIntegrationForm';
import {
  getWebhookPayloadExample,
  WEBHOOK_PAYLOAD_MODEL_EMPTY_MESSAGE,
} from '@/sections/@dashboard/webhooks/webhookPayloadExamples';

const defaultFormValues = {
  name: '',
  url: '',
  products: [],
  events: [],
  triggerType: '',
  fields: {
    secret: '',
  },
};

const defaultPlatform = {
  type: 'webhook',
  events: [],
};

const mapProductToOption = (product = {}) => ({
  value: product?.value || product?.id,
  label: product?.label || formatProductNameWithStatus(product?.name || '', product?.status),
});

const fetchProductOptions = (search) =>
  getProductsLookupService({
    page: 1,
    limit: 20,
    search,
    ordering: 'name',
  }).then((productsData) => productsData.products.map(mapProductToOption));

export default function AddWebhookModal({
  open,
  onClose,
  onSubmit,
  onSave,
  platform,
  productsOptions = [],
  eventsOptions = [],
  initialValues,
  defaultValues,
}) {
  const [showSecret, setShowSecret] = useState(false);
  const notify = useNotify();

  const normalizedPlatform = platform || defaultPlatform;
  const normalizedValues = useMemo(
    () => ({
      ...defaultFormValues,
      ...(defaultValues || initialValues),
      fields: {
        secret:
          defaultValues?.fields?.secret ||
          initialValues?.fields?.secret ||
          defaultValues?.secret ||
          initialValues?.secret ||
          '',
      },
    }),
    [defaultValues, initialValues]
  );

  const { form } = useAppsIntegrationForm({
    platform: normalizedPlatform,
    scope: FormScopes.CREATE,
    defaultValues: normalizedValues,
    fetchProducts: false,
  });

  const { reset } = form;
  const triggerType = form.watch('triggerType');
  const payloadExample = getWebhookPayloadExample(triggerType);
  const payloadExampleText = payloadExample ? JSON.stringify(payloadExample, null, 2) : '';

  useEffect(() => {
    if (open) {
      reset(normalizedValues);
    }
  }, [normalizedValues, open, reset]);

  const resolvedProductsOptions = useMemo(
    () => productsOptions.map(mapProductToOption).filter((option) => option.value && option.label),
    [productsOptions]
  );

  const resolvedEventsOptions = useMemo(() => {
    if (eventsOptions.length > 0) {
      return eventsOptions;
    }

    return (normalizedPlatform.events || []).map(({ custom_id, name }) => ({
      value: custom_id,
      label: name,
    }));
  }, [eventsOptions, normalizedPlatform.events]);

  const handleClose = () => {
    reset(normalizedValues);
    onClose?.();
  };

  const handleSave = async (data) => {
    const submit = onSubmit || onSave;

    await submit?.(data);
    handleClose();
  };

  const handleCopyTemplate = async () => {
    try {
      await navigator.clipboard.writeText(payloadExampleText);
      notify.success('Modelo copiado para a área de transferência!');
    } catch (error) {
      notify.error('Falha ao copiar o modelo.');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <FormProvider methods={form} onSubmit={form.handleSubmit(handleSave)}>
        <DialogTitle
          sx={{
            fontSize: 20,
            fontWeight: 600,
            pb: 1,
          }}
        >
          Adicionar Webhook
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5, pb: 2.5 }}>
          <Stack gap={2} mt={2}>
            <RHFTextField
              name="name"
              label="Nome"
              placeholder="Digite o nome da integração"
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <RHFTextField
              name="url"
              label="URL"
              placeholder="https://"
              size="small"
              type="url"
              InputLabelProps={{ shrink: true }}
            />

            <RHFAsyncAutocomplete
              name="products"
              label="Produtos"
              multiple
              queryKey={['products', normalizedPlatform.type]}
              fetchOptions={fetchProductOptions}
              initialOptions={resolvedProductsOptions}
              minChars={3}
              debounceMs={300}
              noOptionsTextIdle="Digite para pesquisar"
              noOptionsTextEmpty="Não encontrado"
            />

            <RHFMultiSelect
              name="events"
              label=""
              placeholder="Selecione os eventos"
              size="small"
              chip
              checkbox
              options={resolvedEventsOptions}
            />

            <RHFSelect
              name="triggerType"
              size="small"
              label="Tipo de disparo"
              placeholder="Selecione o tipo de disparo"
            >
              <MenuItem value="individual">
                Individual (envia um evento para cada item vendido)
              </MenuItem>
              <MenuItem value="grouped">
                Agrupado (envia um único evento com todos os dados da venda)
              </MenuItem>
            </RHFSelect>

            <Box
              sx={{
                position: 'relative',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
                pt: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  top: -9,
                  left: 12,
                  px: 0.75,
                  color: 'text.secondary',
                  backgroundColor: 'background.paper',
                }}
              >
                Modelo
              </Typography>
              <Tooltip title="Copiar modelo">
                <span>
                  <IconButton
                    size="small"
                    data-testid="copy-model-button"
                    onClick={handleCopyTemplate}
                    disabled={!payloadExampleText}
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      zIndex: 2,
                      pointerEvents: 'auto',
                    }}
                  >
                    <Iconify icon="eva:copy-fill" />
                  </IconButton>
                </span>
              </Tooltip>
              <Box
                sx={{
                  color: 'text.primary',
                  borderRadius: 1,
                }}
              >
                {payloadExample ? (
                  <Scrollbar
                    sx={{
                      maxHeight: 260,
                      '& .simplebar-content': {
                        pl: 3,
                        pr: 6,
                        pb: 2,
                      },
                    }}
                  >
                    <Typography
                      component="pre"
                      variant="body2"
                      sx={{
                        m: 0,
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        pl: 0.5,
                        color: (theme) => (theme.palette.mode === 'dark' ? 'grey.400' : 'grey.700'),
                      }}
                    >
                      {payloadExampleText}
                    </Typography>
                  </Scrollbar>
                ) : (
                  <Box sx={{ px: 2, pb: 2, pr: 6 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {WEBHOOK_PAYLOAD_MODEL_EMPTY_MESSAGE}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <RHFTextField
              name="fields.secret"
              label="Chave secreta do webhook"
              placeholder="Digite a Chave secreta do webhook"
              size="small"
              type={showSecret ? 'text' : 'password'}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowSecret((prev) => !prev)} edge="end">
                      <Iconify icon={showSecret ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            Cancelar
          </Button>
          <LoadingButton variant="contained" type="submit" loading={form.formState.isSubmitting}>
            Salvar
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

AddWebhookModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  onSave: PropTypes.func,
  platform: PropTypes.object,
  productsOptions: PropTypes.array,
  eventsOptions: PropTypes.array,
  initialValues: PropTypes.object,
  defaultValues: PropTypes.object,
};
